"use client";

import { Button } from "@/components/ui/Button";
import { Input, Radio } from "@/components/ui/input";
import Spinner from "./ui/Spinner";

import { DATA_LAYER_EVENT_NAMES } from "@/constants/dataLayer";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import { basketService } from "@/services/basketService";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { sendGTMEvent } from "@next/third-parties/google";

import { useLocale } from "next-intl";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const SERVICE_TYPES = ["Flight", "Bus", "Hotel", "Ferry", "Car"];

const ReservationControl = ({ serviceType = "Flight" }) => {
  const t = useTranslationDetector();
  const locale = useLocale();
  const router = useRouter();

  const serviceOptions = useMemo(
    () => [
      { value: "Flight", label: t("common.flight") },
      { value: "Bus", label: t("common.bus") },
      { value: "Hotel", label: t("common.hotel") },
      { value: "Ferry", label: t("common.ferry") },
      { value: "Car", label: t("common.car") },
    ],
    [t],
  );

  const reservationSchema = useMemo(
    () =>
      z.object({
        serviceType: z.enum(SERVICE_TYPES, {
          required_error: t("common.serviceTypeRequired"),
        }),
        reservationNumber: z
          .string()
          .min(1, t("common.reservationNumberRequired"))
          .min(6, t("common.reservationNumberMin"))
          .max(20, t("common.reservationNumberMax")),
        surname: z
          .string()
          .min(1, t("common.surnameRequired"))
          .min(2, t("common.surnameMin"))
          .max(50, t("common.surnameMax"))
          .regex(/^[a-zA-ZğüşöçıĞÜŞÖÇİ\s]+$/, t("common.surnameOnlyLetters")),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(reservationSchema),
    defaultValues: {
      serviceType,
      reservationNumber: "",
      surname: "",
    },
  });

  const selectedServiceType = watch("serviceType");

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, message: null });

  const setLoadingStatus = useCallback(() => {
    setStatus({ type: "loading", message: t("common.pleaeWait") });
  }, [t]);

  const setErrorStatus = useCallback((message) => {
    setStatus({ type: "error", message });
  }, []);

  const setSuccessStatus = useCallback((message) => {
    setStatus({ type: "success", message });
  }, []);

  const resolveErrorMessage = useCallback(
    (err) => {
      const type = err?.response?.data?.Type;
      const name = err?.response?.data?.Name;

      if (type && name) return t(`${type}.${name}`);

      // fallback
      return t("unexpectedError") || "Beklenmeyen bir hata oluştu.";
    },
    [t],
  );

  const submitCore = useCallback(
    async (data) => {
      setIsLoading(true);
      setLoadingStatus();

      try {
        const payload = {
          ProductType: data.serviceType,
          PnrNo: data.reservationNumber,
          LastName: data.surname,
          LanguageCode: locale,
        };

        const response =
          await basketService.generateBasketHashCodeByPnr(payload);

        if (response?.Code) {
          setSuccessStatus(t("common.pleaseWaitRedirecting"));
          router.push(`/reservation/${response.Code}`);
          return;
        }

        if (response?.Type && response?.Name) {
          setErrorStatus(t(`${response.Type}.${response.Name}`));
          return;
        }

        setErrorStatus(t("common.unexpectedError"));
      } catch (err) {
        setErrorStatus(resolveErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    },
    [
      locale,
      router,
      resolveErrorMessage,
      setErrorStatus,
      setLoadingStatus,
      setSuccessStatus,
      t,
    ],
  );

  const trackReservationAction = useCallback(
    (action, data) => {
      const payload = {
        event: "custom_event",
        event_category: "custom_event",
        pnr_number: data?.reservationNumber,
        reservation_category: selectedServiceType.toLowerCase(),
      };
      if (action === "checkin") {
        sendGTMEvent({
          ...payload,
          event_action: DATA_LAYER_EVENT_NAMES.ONLINE_CHECKIN,
          flight_number: data?.reservationNumber,
        });
      } else {
        sendGTMEvent({
          ...payload,
          event_action: DATA_LAYER_EVENT_NAMES.CHECK_RESERVATION,
        });
      }
    },
    [selectedServiceType],
  );

  const onSubmit = useCallback(
    () => async (data, event) => {
      const action = event?.nativeEvent?.submitter?.value || "search";
      const isCheckIn =
        action === "checkin" && selectedServiceType === "Flight";
      if (isCheckIn) {
        trackReservationAction("checkin", data);
      } else {
        trackReservationAction("search", data);
      }
      await submitCore(data);
    },
    [submitCore, selectedServiceType, trackReservationAction],
  );

  const showFlightOnlyActions = selectedServiceType === "Flight";

  const statusView = useMemo(() => {
    if (!status.type || !status.message) return null;

    if (status.type === "loading") {
      return (
        <div className="border-info/25 bg-info/5 text-info flex items-center gap-3 rounded-xl border px-4 py-3 text-sm">
          <Spinner className="mt-0.5 h-4 w-4 animate-spin" />
          <div className="leading-5">
            <div className="text-info/70 mt-0.5 text-xs">{status.message}</div>
          </div>
        </div>
      );
    }

    if (status.type === "error") {
      return (
        <div className="border-danger/25 bg-danger/5 text-danger flex items-center gap-3 rounded-xl border px-4 py-3 text-sm">
          <FontAwesomeIcon
            icon={byPrefixAndName.far["circle-exclamation"]}
            className="text-danger/90 mt-0.5 h-5 w-5"
          />
          <div className="leading-5">
            <div className="text-danger/80 mt-0.5 text-sm">
              {status.message}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="border-success/25 bg-success/5 text-success flex items-center gap-3 rounded-xl border px-4 py-3 text-sm">
        <FontAwesomeIcon
          icon={byPrefixAndName.far["circle-check"]}
          className="text-success/90 mt-0.5 h-5 w-5"
        />
        <div className="leading-5">
          <div className="text-success/80 mt-0.5 text-sm">{status.message}</div>
        </div>
      </div>
    );
  }, [status]);

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit())}>
      {/* Service Type */}
      <div className="space-y-3">
        {/* <label className="block text-sm font-medium text-gray-700">
          {t("common.serviceTypeLabel")}
        </label> */}

        <div className="flex w-full justify-between gap-2">
          {serviceOptions.map((option) => (
            <Radio
              key={option.value}
              {...register("serviceType")}
              value={option.value}
              checked={selectedServiceType === option.value}
              disabled={isLoading}
            >
              {option.label}
            </Radio>
          ))}
        </div>

        {errors.serviceType && (
          <p className="text-danger mt-1 text-xs">
            {errors.serviceType.message}
          </p>
        )}
      </div>

      {/* Inputs */}
      <div className="space-y-4">
        <Input
          {...register("reservationNumber")}
          label={t("common.reservationNumberLabel")}
          error={errors.reservationNumber?.message}
          required
          // placeholder={t("common.reservationNumberPlaceholder")}
          disabled={isLoading}
        />

        <Input
          {...register("surname")}
          label={t("common.surnameLabel")}
          error={errors.surname?.message}
          required
          // placeholder={t("common.surnamePlaceholder")}
          disabled={isLoading}
        />
      </div>

      {/* Status */}
      {statusView}

      {/* Actions */}
      <div className="grid grid-cols-1 gap-2">
        <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
          {t("common.reservationCheck")}
        </Button>

        {/* {showFlightOnlyActions && (
          <>
            <Button
              type="submit"
              name="action"
              value="checkin"
              variant="outline"
              size="lg"
              className="w-full"
              disabled={isLoading}
            >
              {t("common.checkIn")}
            </Button>

            <Button
              type="submit"
              name="action"
              value="changeFlight"
              variant="outline"
              size="lg"
              className="w-full"
              disabled={isLoading}
            >
              {t("common.changeFlight")}
            </Button>
          </>
        )}

        <Button
          type="submit"
          name="action"
          value="cancelRefund"
          color="danger"
          variant="outline"
          size="lg"
          className="w-full"
          disabled={isLoading}
        >
          {t("common.cancelRefund")}
        </Button> */}
      </div>
    </form>
  );
};

export default ReservationControl;
